/* Bill Locks — auto-sweep Spending into a sealed lock for an upcoming bill. */
import { useCallback, useEffect, useState } from "react";
import { api, formatErr, fmtMoney } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Receipt, Plus, Lock, Unlock, CalendarClock, Pencil, Trash2, Pause, Play, X, Banknote, RefreshCw } from "lucide-react";

const fmtDate = (ymd) => new Date(ymd + "T12:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
const dayBefore = (ymd) => { const d = new Date(ymd + "T12:00:00"); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); };

const PHASE = {
  filling:   { label: "Filling",  cls: "bg-sand-light text-moss" },
  sealed:    { label: "Sealed",   cls: "bg-clay-light text-clay" },
  ready:     { label: "Ready",    cls: "bg-forest text-white" },
  expired:   { label: "Missed",   cls: "bg-sand-light text-sage" },
  released:  { label: "Released", cls: "bg-ink text-white" },
  cancelled: { label: "Cancelled", cls: "bg-sand-light text-sage" },
};

export function BillLockDialog({ existing, initial, onClose, onDone }) {
  const [name, setName] = useState(existing?.name || initial?.name || "");
  const [amount, setAmount] = useState(existing ? String(existing.amount) : initial?.amount ? String(initial.amount) : "");
  const [dueDate, setDueDate] = useState(existing?.due_date || "");
  const [unlockDate, setUnlockDate] = useState(existing?.unlock_date || "");
  const [touchedUnlock, setTouchedUnlock] = useState(!!existing);
  const [recurrence, setRecurrence] = useState(existing?.recurrence || (existing?.recurring ? "monthly" : ""));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onDue = (v) => { setDueDate(v); if (!touchedUnlock && v) setUnlockDate(dayBefore(v)); };

  const submit = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try {
      const body = { name, amount: parseFloat(amount), due_date: dueDate, unlock_date: unlockDate || undefined, recurring: !!recurrence, recurrence: recurrence || (existing ? "" : undefined) };
      if (existing) await api.patch(`/bill-locks/${existing.id}`, body);
      else await api.post("/bill-locks", body);
      onDone();
    } catch (err) { setError(formatErr(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="bill-lock-dialog">
      <form onSubmit={submit} className="bg-cream rounded-2xl p-6 w-full max-w-md shadow-xl border border-sand">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-sage">Bill lock</div>
            <h3 className="font-serif text-2xl mt-0.5">{existing ? "Edit bill lock" : "Seal money for a bill"}</h3>
            {initial?.context && <div className="text-xs text-gold mt-1" data-testid="bill-lock-context">{initial.context}</div>}
          </div>
          <button type="button" onClick={onClose} className="text-moss hover:text-ink" data-testid="bill-lock-close"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-moss mb-4">
          Everything in Spending — and every deposit that lands — is swept into this lock until the bill is covered. It opens on your unlock day.
        </p>
        <label className="block text-[10px] uppercase tracking-wider font-semibold text-sage mb-1">Bill name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} required autoFocus
          placeholder="Phone bill · Course book · Rent" data-testid="bill-lock-name"
          className="w-full bg-linen border border-sand rounded-md px-3 py-2 text-sm mb-3 focus:outline-none focus:border-forest" />
        <label className="block text-[10px] uppercase tracking-wider font-semibold text-sage mb-1">Amount</label>
        <input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required
          placeholder="95.00" data-testid="bill-lock-amount"
          className="w-full bg-linen border border-sand rounded-md px-3 py-2 text-sm font-mono mb-3 focus:outline-none focus:border-forest" />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-semibold text-sage mb-1">Due date</label>
            <input type="date" value={dueDate} onChange={(e) => onDue(e.target.value)} required data-testid="bill-lock-due"
              className="w-full bg-linen border border-sand rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-forest" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-semibold text-sage mb-1">Unlock day</label>
            <input type="date" value={unlockDate} max={dueDate || undefined}
              onChange={(e) => { setTouchedUnlock(true); setUnlockDate(e.target.value); }} required data-testid="bill-lock-unlock"
              className="w-full bg-linen border border-sand rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-forest" />
          </div>
        </div>
        <div className="text-[10px] text-sage mt-1.5">Defaults to the day before it&apos;s due. You&apos;ll get a prompt to release it that morning.</div>
        <div className="mt-4 bg-linen border border-sand rounded-lg px-3 py-2.5" data-testid="bill-lock-recurring-row">
          <div className="text-xs font-medium text-ink inline-flex items-center gap-1"><RefreshCw className="w-3 h-3 text-forest" /> Recurring bill</div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {[["", "One-time"], ["monthly", "Recurring monthly"], ["yearly", "Recurring yearly"]].map(([v, l]) => (
              <button type="button" key={v || "none"} onClick={() => setRecurrence(v)} aria-pressed={recurrence === v}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${recurrence === v ? "bg-forest text-white border-forest" : "bg-white border-sand text-moss hover:border-ink"}`}
                data-testid={`bill-lock-recurrence-${v || "none"}`}>{l}</button>
            ))}
          </div>
          <div className="text-[11px] text-moss mt-2">
            {recurrence === "yearly" ? `Paced into equal monthly sweeps so the full amount is ready a week before it's due${amount && dueDate ? ` — about ${fmtMoney(parseFloat(amount) / Math.max(1, Math.round((new Date(dueDate) - Date.now()) / 2629800000)))} a month` : ""}. After release you pick when next year's lock starts.` :
             recurrence === "monthly" ? "After you release this lock, you pick the day next month's lock starts filling — same amount, due date one month later." :
             "Locks once for this bill and that's it."}
          </div>
        </div>
        {error && <div className="text-sm text-clay mt-3" data-testid="bill-lock-error">{error}</div>}
        <button type="submit" disabled={busy}
          className="mt-5 w-full bg-forest text-white rounded-md py-2.5 text-sm font-medium hover:bg-forest-deep disabled:opacity-50 transition-colors"
          data-testid="bill-lock-submit">
          {busy ? "Saving…" : existing ? "Save changes" : "Create bill lock"}
        </button>
      </form>
    </div>
  );
}

function BillLockCard({ b, onEdit, onToggle, onCancel, onRelease, onEarlyUnlock, onAdminDelete }) {
  const ph = PHASE[b.phase] || PHASE.filling;
  const open = b.status === "ACTIVE";
  return (
    <div className={`bg-linen border rounded-xl p-5 ${b.phase === "ready" ? "border-forest" : "border-sand"}`} data-testid={`bill-lock-${b.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage truncate">{b.name}</div>
          <div className="font-mono text-2xl text-ink mt-1 tabular-nums" data-testid={`bill-lock-locked-${b.id}`}>
            {fmtMoney(b.locked_amount)} <span className="text-sm text-sage">/ {fmtMoney(b.amount)}</span>
          </div>
        </div>
        <span className="flex items-center gap-1.5 shrink-0"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider shrink-0 ${ph.cls}`}
              data-testid={`bill-lock-phase-${b.id}`}>
          {b.phase === "ready" || b.phase === "released" ? <Unlock className="w-2.5 h-2.5" /> : <Lock className="w-2.5 h-2.5" />}
          {b.waiting_to_start ? `Starts ${fmtDate(b.start_date)}` : ph.label}{open && !b.active && b.phase === "filling" ? " · paused" : ""}
        </span>
        {b.recurring && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-forest/10 text-forest text-[10px] font-medium uppercase tracking-wider shrink-0" data-testid={`bill-lock-recurring-pill-${b.id}`}><RefreshCw className="w-2.5 h-2.5" /> {b.recurrence === "yearly" ? "Yearly" : "Monthly"}</span>}</span>
      </div>
      <div className="mt-3 h-1.5 rounded-full bg-sand-soft overflow-hidden">
        <div className={`h-full transition-all ${b.funded_pct >= 100 ? "bg-forest" : "bg-gold"}`} style={{ width: `${Math.min(100, b.funded_pct)}%` }} />
      </div>
      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap text-[11px] text-moss">
        <span className="inline-flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5 text-sage" strokeWidth={1.5} />
          Due <span className="font-mono">{fmtDate(b.due_date)}</span>
          {open && <span className="text-sage">· {b.days_until_due} day{b.days_until_due === 1 ? "" : "s"}</span>}
        </span>
        <span>Unlocks <span className="font-mono">{fmtDate(b.unlock_date)}</span></span>
      </div>
      {b.pace && (
        <div className="mt-2 text-[11px] text-forest bg-forest/5 border border-forest/15 rounded-md px-2.5 py-1.5" data-testid={`bill-lock-pace-${b.id}`}>
          Paced: <span className="font-mono">{fmtMoney(b.pace.per_month)}</span>/month × {b.pace.months} · {fmtMoney(b.pace.target_now)} unlocked for sweeping so far · fully funded by <span className="font-mono">{fmtDate(b.pace.ready_by)}</span>
        </div>
      )}
      {open && (
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          {b.phase === "ready" ? (
            <>
              <button onClick={() => onRelease(b)} className="bg-forest text-white rounded-md px-4 py-2 text-xs font-medium hover:bg-forest-deep transition-colors inline-flex items-center gap-1.5"
                data-testid={`bill-lock-release-${b.id}`}>
                <Unlock className="w-3 h-3" /> Release {fmtMoney(b.locked_amount)} to Spending
              </button>
              <button onClick={() => onEdit(b)} className="border border-sand rounded-md px-3 py-1.5 text-xs font-medium hover:bg-sand-light inline-flex items-center gap-1" data-testid={`bill-lock-edit-${b.id}`}>
                <Pencil className="w-3 h-3" /> Edit
              </button>
              {onAdminDelete && b.locked_amount > 0 && (
                <button onClick={() => onAdminDelete(b)} className="text-clay rounded-md px-3 py-1.5 text-xs font-medium hover:bg-clay-light inline-flex items-center gap-1" data-testid={`bill-lock-admin-delete-${b.id}`}>
                  <Trash2 className="w-3 h-3" /> Release & delete
                </button>
              )}
            </>
          ) : (
            <>
              <button onClick={() => onEdit(b)} className="border border-sand rounded-md px-3 py-1.5 text-xs font-medium hover:bg-sand-light inline-flex items-center gap-1" data-testid={`bill-lock-edit-${b.id}`}>
                <Pencil className="w-3 h-3" /> Edit
              </button>
              {b.phase === "filling" && (
                <button onClick={() => onToggle(b)} className="border border-sand rounded-md px-3 py-1.5 text-xs font-medium hover:bg-sand-light inline-flex items-center gap-1" data-testid={`bill-lock-toggle-${b.id}`}>
                  {b.active ? <><Pause className="w-3 h-3" /> Pause filling</> : <><Play className="w-3 h-3" /> Resume</>}
                </button>
              )}
              {b.locked_amount === 0 && (
                <button onClick={() => onCancel(b)} className="text-clay rounded-md px-3 py-1.5 text-xs font-medium hover:bg-clay-light inline-flex items-center gap-1" data-testid={`bill-lock-cancel-${b.id}`}>
                  <Trash2 className="w-3 h-3" /> Remove
                </button>
              )}
              {onEarlyUnlock && b.locked_amount > 0 && (
                <button onClick={() => onEarlyUnlock(b)} className="border border-forest text-forest rounded-md px-3 py-1.5 text-xs font-medium hover:bg-forest hover:text-white inline-flex items-center gap-1 transition-colors" data-testid={`bill-lock-early-unlock-${b.id}`}>
                  <Unlock className="w-3 h-3" /> Unlock {fmtMoney(b.locked_amount)} early
                </button>
              )}
              {onAdminDelete && b.locked_amount > 0 && (
                <button onClick={() => onAdminDelete(b)} className="text-clay rounded-md px-3 py-1.5 text-xs font-medium hover:bg-clay-light inline-flex items-center gap-1" data-testid={`bill-lock-admin-delete-${b.id}`}>
                  <Trash2 className="w-3 h-3" /> Release & delete
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function DemoDepositButton({ onDone, onError }) {
  const [amt, setAmt] = useState("250");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.post("/bill-locks/demo-deposit", { amount: parseFloat(amt) }); onDone(); }
    catch (e) { onError(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="inline-flex items-center gap-1.5" data-testid="bill-lock-demo-deposit">
      <input type="number" min="1" step="1" value={amt} onChange={(e) => setAmt(e.target.value)}
        className="w-20 bg-linen border border-sand rounded-md px-2 py-1.5 text-xs font-mono focus:outline-none" data-testid="bill-lock-demo-amount" />
      <button onClick={go} disabled={busy} className="border border-gold text-gold rounded-md px-3 py-1.5 text-xs font-medium hover:bg-gold hover:text-white disabled:opacity-50 transition-colors inline-flex items-center gap-1"
        data-testid="bill-lock-demo-deposit-btn">
        <Banknote className="w-3.5 h-3.5" /> Simulate paycheck
      </button>
    </div>
  );
}

export default function BillLocksSection({ onChanged }) {
  const { user } = useAuth();
  const [data, setData] = useState({ bill_locks: [], spending_available: 0 });
  const [dialog, setDialog] = useState(null);
  const [msg, setMsg] = useState("");
  const [sweeping, setSweeping] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/bill-locks"); setData(data); } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onRefresh = () => load();
    window.addEventListener("bill-locks:refresh", onRefresh);
    return () => window.removeEventListener("bill-locks:refresh", onRefresh);
  }, [load]);

  const refresh = () => { load(); onChanged?.(); };
  const run = async (fn, okMsg) => {
    setMsg("");
    try { await fn(); if (okMsg) setMsg(okMsg); refresh(); }
    catch (e) { setMsg(formatErr(e.response?.data?.detail) || e.message); }
  };
  const sweepNow = async () => {
    setSweeping(true);
    await run(async () => {
      const { data } = await api.post("/bill-locks/sweep");
      setMsg(data.swept > 0 ? `Swept ${fmtMoney(data.swept)} from Spending into your bill locks.` : "Nothing to sweep right now.");
    });
    setSweeping(false);
  };

  const open = data.bill_locks.filter((b) => b.status === "ACTIVE");
  const closed = data.bill_locks.filter((b) => b.status !== "ACTIVE");

  return (
    <section className="mt-10" data-testid="bill-locks-section">
      <div className="flex items-end justify-between mb-4 gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Bill Locks</div>
          <h2 className="font-serif text-2xl tracking-tight mt-1">Bills pay themselves first.</h2>
          <p className="text-xs text-moss mt-1 max-w-xl">
            {data.auto_sweep
              ? <>Name a bill, its due date and an unlock day. Spending and every incoming deposit are swept into a sealed lock — soonest bill first — until it&apos;s covered.</>
              : <>Auto-fill is off. Nothing leaves Spending for a bill until you tap <strong className="text-ink">Sweep now</strong>. Sealed money unlocks on its date.</>}
          </p>
          <button onClick={() => run(async () => { await api.patch("/bill-locks/settings/auto-sweep", { enabled: !data.auto_sweep }); }, data.auto_sweep ? "Auto-fill turned off — bills only fill when you say so." : "Auto-fill turned on.")}
            className={`mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium rounded-full px-2.5 py-1 border transition-colors ${data.auto_sweep ? "border-forest/40 text-forest bg-mint" : "border-sand text-moss hover:text-ink"}`}
            data-testid="bill-lock-auto-sweep-toggle" aria-pressed={!!data.auto_sweep}>
            {data.auto_sweep ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />} Auto-fill from Spending: {data.auto_sweep ? "On" : "Off"}
          </button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-right mr-1">
            <div className="text-[9px] uppercase tracking-[0.2em] font-semibold text-sage">Spending now</div>
            <div className="font-mono text-sm text-ink" data-testid="bill-lock-spending-available">{fmtMoney(data.spending_available)}</div>
          </div>
          {user?.demo && <DemoDepositButton onDone={() => { setMsg(""); refresh(); }} onError={setMsg} />}
          <button onClick={sweepNow} disabled={sweeping || open.length === 0}
            className="inline-flex items-center gap-1.5 border border-sand rounded-md px-3 py-2 text-xs font-medium hover:bg-linen disabled:opacity-40 transition-colors"
            data-testid="bill-lock-sweep-btn" title="Sweep Spending into bill locks now">
            <RefreshCw className={`w-3.5 h-3.5 ${sweeping ? "animate-spin" : ""}`} strokeWidth={1.5} /> Sweep now
          </button>
          <button onClick={() => setDialog({})}
            className="bg-forest text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-forest-deep transition-colors inline-flex items-center gap-1.5"
            data-testid="new-bill-lock-btn">
            <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> New bill lock
          </button>
        </div>
      </div>

      {msg && <div className="mb-3 px-4 py-2.5 rounded-lg bg-linen border border-sand text-xs text-ink" data-testid="bill-lock-message">{msg}</div>}

      {open.length === 0 ? (
        <div className="border border-dashed border-sand rounded-xl p-6 text-center text-sm text-sage flex flex-col items-center gap-2" data-testid="bill-locks-empty">
          <Receipt className="w-5 h-5 text-gold" strokeWidth={1.5} />
          No bill locks yet. Add your next bill and let Spending fill it automatically.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3" data-testid="bill-locks-list">
          {open.map((b) => (
            <BillLockCard key={b.id} b={b}
              onEdit={(x) => setDialog({ existing: x })}
              onToggle={(x) => run(() => api.patch(`/bill-locks/${x.id}`, { active: !x.active }))}
              onCancel={(x) => run(() => api.delete(`/bill-locks/${x.id}`))}
              onRelease={(x) => run(async () => { const { data: r } = await api.post(`/bill-locks/${x.id}/release`); setMsg(r.partial ? `Released ${fmtMoney(r.released)} of ${fmtMoney(x.locked_amount)} from ${x.name} — that's all the bank had available. ${fmtMoney(r.remaining)} stays on the ledger; edit the amount down or use Release & delete.` : `Released ${fmtMoney(r.released)} to Spending for ${x.name}.${x.recurring ? ` Pick when next ${x.recurrence === "yearly" ? "year" : "month"}'s lock starts filling below.` : ""}`); })}
              onEarlyUnlock={data.early_unlock_available ? (x) => { if (window.confirm(`Release ${fmtMoney(x.locked_amount)} from "${x.name}" to Spending now, before its unlock date? If the bank holds less, whatever is available is released and the rest stays locked.`)) run(async () => { const { data: r } = await api.post(`/admin/bill-locks/${x.id}/early-unlock`); setMsg(r.partial ? `Released ${fmtMoney(r.released)} of ${fmtMoney(x.locked_amount)} from ${x.name} — that's all the bank had available. ${fmtMoney(r.remaining)} stays locked; tap again later for the rest.` : `${fmtMoney(r.released)} from ${x.name} released early to Spending.`); }); } : null}
              onAdminDelete={data.early_unlock_available ? (x) => { if (window.confirm(`Delete "${x.name}"? Whatever the bank actually holds for it goes to Spending first; any ledger amount the bank never had is written off.`)) run(async () => { const { data: r } = await api.delete(`/admin/bill-locks/${x.id}`); setMsg(`${x.name} deleted. ${r.released > 0 ? `${fmtMoney(r.released)} released to Spending.` : "Nothing was available at the bank to release."}${r.written_off > 0 ? ` ${fmtMoney(r.written_off)} written off (never held at the bank).` : ""}`); }); } : null} />
          ))}
        </div>
      )}

      {closed.length > 0 && (
        <details className="mt-3 text-xs text-moss" data-testid="bill-locks-history">
          <summary className="cursor-pointer select-none">History · {closed.length}</summary>
          <div className="mt-2 grid md:grid-cols-2 gap-2">
            {closed.map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-2 flex-wrap bg-linen border border-sand rounded-lg px-3 py-2" data-testid={`bill-lock-closed-${b.id}`}>
                <span className="truncate">{b.name} · due {fmtDate(b.due_date)}</span>
                <span className="font-mono">{fmtMoney(b.locked_amount)} · {PHASE[b.phase]?.label || b.status}</span>
                {b.next_cycle_needed && <NextCyclePicker b={b} onDone={refresh} />}
              </div>
            ))}
          </div>
        </details>
      )}

      {dialog && <BillLockDialog existing={dialog.existing} onClose={() => setDialog(null)} onDone={() => { setDialog(null); refresh(); }} />}
    </section>
  );
}

function NextCyclePicker({ b, onDone }) {
  const [start, setStart] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const go = async () => {
    setBusy(true); setErr("");
    try { await api.post(`/bill-locks/${b.id}/next-cycle`, { start_date: start }); onDone(); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="w-full mt-1 flex items-center gap-2 flex-wrap text-[11px]" data-testid={`bill-lock-next-cycle-${b.id}`}>
      <span className="text-ink">Next {b.recurrence === "yearly" ? "year" : "month"}&apos;s {b.name}: start filling on</span>
      <input type="date" value={start} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setStart(e.target.value)} className="bg-white border border-sand rounded-md px-2 py-1 font-mono text-[11px]" data-testid={`bill-lock-next-start-${b.id}`} />
      <button onClick={go} disabled={busy} className="bg-ink text-cream rounded-md px-2.5 py-1 font-medium disabled:opacity-50" data-testid={`bill-lock-next-save-${b.id}`}>{busy ? "Setting…" : `Set up next ${b.recurrence === "yearly" ? "year" : "month"}`}</button>
      {err && <span className="text-clay">{err}</span>}
    </div>
  );
}
